import './registrar-bonus-modal.js';
import './bonus-acoes-modal.js';
import type { BonusPg, Pessoa, StatusBonusPg } from '@pgcustom/shared-types';
import { formatBRL, centavosParaReais } from '@pgcustom/shared-utils';
import type { Resultado } from '../../core/resultado.js';
import type { PgTable, PgTableColumn, PgTableRow, PgKpiCard } from '@pgcustom/design-system';
import type { SocietarioApi } from '../societario/societario.api.js';
import type { PermissoesBonusPg } from './bonus-pg.permissoes.js';
import type { ErpRegistrarBonusModal } from './registrar-bonus-modal.js';
import type { ErpBonusAcoesModal } from './bonus-acoes-modal.js';

const COLUNAS: PgTableColumn[] = [
  { key: 'pessoa', label: 'Pessoa' },
  { key: 'competencia', label: 'Competência' },
  { key: 'motivo', label: 'Motivo' },
  { key: 'valor', label: 'Valor', align: 'right' },
  { key: 'status', label: 'Status' },
];

const TOM_STATUS: Record<StatusBonusPg, 'neutro' | 'sucesso' | 'alerta' | 'erro' | 'destaque'> = {
  proposto: 'alerta',
  aprovado: 'destaque',
  pago: 'sucesso',
  rejeitado: 'erro',
  cancelado: 'neutro',
};

const TEXTO_STATUS: Record<StatusBonusPg, string> = {
  proposto: 'Proposto',
  aprovado: 'Aprovado',
  pago: 'Pago',
  rejeitado: 'Rejeitado',
  cancelado: 'Cancelado',
};

const OPCOES_FILTRO_STATUS: Array<{ valor: StatusBonusPg | ''; rotulo: string }> = [
  { valor: '', rotulo: 'Todos os status' },
  { valor: 'proposto', rotulo: 'Proposto' },
  { valor: 'aprovado', rotulo: 'Aprovado' },
  { valor: 'pago', rotulo: 'Pago' },
  { valor: 'rejeitado', rotulo: 'Rejeitado' },
  { valor: 'cancelado', rotulo: 'Cancelado' },
];

/**
 * `<erp-bonus-pg-page>` — administração do Bônus PG (owner, "fase de
 * finalização" §5): visualizar, registrar, aprovar, rejeitar, pagar e
 * cancelar. DISCRICIONÁRIO por definição — esta tela nunca sugere,
 * calcula ou pré-preenche um valor; toda decisão (quem, quanto, por quê)
 * é digitada por um humano em `<erp-registrar-bonus-modal>`. Mesmo
 * backend/RBAC já usado por aportes/participação/retiradas
 * (`societario` — `admin`/`financeiro`), nenhuma segunda contabilidade:
 * pagamento sempre referencia uma transação real já lançada em
 * Financeiro (`<erp-bonus-acoes-modal>`).
 */
export class ErpBonusPgPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _societarioApi: SocietarioApi | null = null;
  private _permissoes: PermissoesBonusPg = { ler: false, criar: false, atualizar: false };
  private _usuarioId: string | null = null;

  private resultadoBonus: Resultado<BonusPg[]> = { status: 'carregando' };
  private bonusAtuais: BonusPg[] = [];
  private pessoas: Pessoa[] = [];
  private nomesPorPessoaId: Map<string, string> = new Map();
  private filtroStatus: StatusBonusPg | '' = '';

  private modalRegistrar!: ErpRegistrarBonusModal;
  private modalAcoes!: ErpBonusAcoesModal;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set societarioApi(valor: SocietarioApi) {
    this._societarioApi = valor;
    if (this.isConnected) void this.carregar();
  }

  set permissoes(valor: PermissoesBonusPg) {
    this._permissoes = valor;
    if (this.isConnected) this.renderEsqueleto();
  }

  /** id do usuário logado — repassado ao modal de ações como `aprovadorId`. */
  set usuarioId(valor: string | null) {
    this._usuarioId = valor;
  }

  connectedCallback(): void {
    this.renderEsqueleto();
    if (this._societarioApi) void this.carregar();
  }

  private renderEsqueleto(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        .topo { display: flex; align-items: center; justify-content: space-between; gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-5, 24px); flex-wrap: wrap; }
        h1 { font-family: var(--pg-font-familia-display, system-ui); font-size: var(--pg-font-size-xl, 1.5rem); color: var(--pg-color-texto-primario, #f2f3f5); margin: 0; }
        .subtitulo { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin-top: 2px; }
        .kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-4, 16px); }
        .filtros { display: flex; align-items: center; gap: var(--pg-space-3, 12px); margin-bottom: var(--pg-space-4, 16px); }
        .filtros select {
          background: var(--pg-color-bg-base, #121417);
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-md, 10px);
          color: var(--pg-color-texto-primario, #f2f3f5);
          padding: var(--pg-space-2, 8px) var(--pg-space-3, 12px);
          font-size: var(--pg-font-size-sm, 0.875rem);
        }
        .dica { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-desabilitado, #5c626b); margin-top: var(--pg-space-3, 12px); }
      </style>
      <div class="topo">
        <div>
          <h1>Bônus PG</h1>
          <div class="subtitulo">Reconhecimento discricionário — nenhum valor é calculado automaticamente, toda decisão é registrada manualmente</div>
        </div>
        ${this._permissoes.criar ? '<pg-button id="botao-registrar" variant="primary">Registrar bônus</pg-button>' : ''}
      </div>

      <div class="kpis">
        <pg-kpi-card id="kpi-propostos" label="Propostos" icone="bonus" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-aprovados" label="Aprovados (a pagar)" icone="bonus" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-pagos" label="Pagos" icone="bonus" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-total-pago" label="Total pago" icone="dinheiro" estado="carregando"></pg-kpi-card>
      </div>

      <div class="filtros">
        <select id="filtro-status">
          ${OPCOES_FILTRO_STATUS.map((o) => `<option value="${o.valor}" ${o.valor === this.filtroStatus ? 'selected' : ''}>${escaparHtml(o.rotulo)}</option>`).join('')}
        </select>
      </div>

      <pg-card padding="compacto">
        <div id="bonus"></div>
      </pg-card>
      ${this._permissoes.atualizar ? '<p class="dica">Clique em um bônus para aprovar, rejeitar, pagar ou cancelar.</p>' : ''}

      <erp-registrar-bonus-modal id="modal-registrar"></erp-registrar-bonus-modal>
      <erp-bonus-acoes-modal id="modal-acoes"></erp-bonus-acoes-modal>
    `;

    this.modalRegistrar = this.root.querySelector('#modal-registrar') as ErpRegistrarBonusModal;
    this.modalAcoes = this.root.querySelector('#modal-acoes') as ErpBonusAcoesModal;
    if (this._societarioApi) {
      this.modalRegistrar.societarioApi = this._societarioApi;
      this.modalAcoes.societarioApi = this._societarioApi;
    }
    this.modalAcoes.aprovadorId = this._usuarioId;
    this.modalRegistrar.aoRegistrar = () => void this.carregar();
    this.modalAcoes.aoAtualizar = () => void this.carregar();

    this.root.querySelector('#botao-registrar')?.addEventListener('pg-click', () => {
      this.modalRegistrar.abrir(this.pessoas);
    });

    this.root.querySelector('#filtro-status')?.addEventListener('change', (evento) => {
      this.filtroStatus = (evento.target as HTMLSelectElement).value as StatusBonusPg | '';
      void this.carregar();
    });

    this.root.querySelector('#bonus')?.addEventListener('pg-row-click', (evento) => {
      if (!this._permissoes.atualizar) return;
      const { index } = (evento as CustomEvent<{ row: PgTableRow; index: number }>).detail;
      const bonus = this.bonusAtuais[index];
      if (bonus) this.modalAcoes.abrir(bonus, this.nomesPorPessoaId.get(bonus.pessoaId) ?? 'pessoa desconhecida');
    });

    this.atualizar();
  }

  private async carregar(): Promise<void> {
    const api = this._societarioApi;
    if (!api) return;

    if (!this._permissoes.ler) {
      this.resultadoBonus = { status: 'sem-permissao' };
      this.atualizar();
      return;
    }

    this.resultadoBonus = { status: 'carregando' };
    this.atualizar();

    if (this._permissoes.criar) {
      const resultadoPessoas = await api.listarPessoas();
      this.pessoas = resultadoPessoas.status === 'ok' ? resultadoPessoas.dado : [];
      this.nomesPorPessoaId = new Map(this.pessoas.map((p) => [p.id, p.nome]));
    }

    this.resultadoBonus = await api.listarBonus(this.filtroStatus ? { status: this.filtroStatus } : {});
    this.renderEsqueleto();
  }

  private atualizar(): void {
    const container = this.root.querySelector('#bonus');
    if (!container) return;

    if (this.resultadoBonus.status === 'ok') {
      this.bonusAtuais = this.resultadoBonus.dado;
      container.innerHTML = '<pg-table></pg-table>';
      const tabela = container.querySelector('pg-table') as PgTable;
      tabela.columns = COLUNAS;
      tabela.rows = this.bonusAtuais.map((b) => ({
        pessoa: this.nomesPorPessoaId.get(b.pessoaId) ?? b.pessoaId.slice(0, 8) + '…',
        competencia: b.competencia,
        motivo: b.motivo,
        valor: formatBRL(centavosParaReais(b.valorCentavos)),
        status: { texto: TEXTO_STATUS[b.status], tom: TOM_STATUS[b.status] },
      }));
      this.atualizarKpis(this.bonusAtuais);
      return;
    }

    this.bonusAtuais = [];
    const { variant, titulo, descricao } = estadoVazio(this.resultadoBonus);
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
    for (const id of ['kpi-propostos', 'kpi-aprovados', 'kpi-pagos', 'kpi-total-pago']) {
      const kpi = this.root.querySelector(`#${id}`) as PgKpiCard | null;
      if (!kpi) continue;
      if (this.resultadoBonus.status === 'vazio') {
        kpi.setAttribute('estado', 'ok');
        kpi.setAttribute('valor', id === 'kpi-total-pago' ? formatBRL(0) : '0');
      } else {
        kpi.setAttribute('estado', variant === 'carregando' ? 'carregando' : 'indisponivel');
      }
    }
  }

  private atualizarKpis(bonus: BonusPg[]): void {
    const propostos = bonus.filter((b) => b.status === 'proposto').length;
    const aprovados = bonus.filter((b) => b.status === 'aprovado').length;
    const pagos = bonus.filter((b) => b.status === 'pago');
    const totalPagoCentavos = pagos.reduce((soma, b) => soma + b.valorCentavos, 0);

    (this.root.querySelector('#kpi-propostos') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-propostos') as PgKpiCard | null)?.setAttribute('valor', String(propostos));
    (this.root.querySelector('#kpi-aprovados') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-aprovados') as PgKpiCard | null)?.setAttribute('valor', String(aprovados));
    (this.root.querySelector('#kpi-pagos') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-pagos') as PgKpiCard | null)?.setAttribute('valor', String(pagos.length));
    (this.root.querySelector('#kpi-total-pago') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-total-pago') as PgKpiCard | null)?.setAttribute('valor', formatBRL(centavosParaReais(totalPagoCentavos)));
  }
}

function estadoVazio(resultado: Resultado<BonusPg[]>): { variant: string; titulo: string; descricao: string } {
  switch (resultado.status) {
    case 'carregando':
      return { variant: 'carregando', titulo: 'Bônus PG', descricao: '' };
    case 'sem-permissao':
      return { variant: 'sem-permissao', titulo: 'Bônus PG', descricao: 'você não tem permissão para ver este dado' };
    case 'erro':
      return { variant: 'erro', titulo: 'Bônus PG', descricao: resultado.mensagem };
    default:
      return { variant: 'vazio', titulo: 'Nenhum bônus registrado', descricao: 'bônus aparecem aqui quando registrados manualmente — nenhum é gerado automaticamente' };
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-bonus-pg-page', ErpBonusPgPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-bonus-pg-page': ErpBonusPgPage;
  }
}
